import React, { useEffect, useRef, useState } from "react";
import { ChevronRight, ArrowRight, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import api from "../../api/api";
import toast from "react-hot-toast";
import gsap from "gsap";

const HERO_IMAGE = "/assets/images/herobg.jpeg";

const ProjectsPage = () => {
  const pageRef = useRef(null);

  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const loadCategories = async () => {
    try {
      const res = await api.get("/project-categories");
      setCategories(res.data || []);
    } catch (err) {
      toast.error(err.cleanMessage || "Failed to load project categories");
    }
  };

  const loadProjects = async () => {
    try {
      setLoading(true);

      const query = new URLSearchParams({
        page,
        limit: 6,
      });

      if (selectedCategory) {
        query.append("category_id", selectedCategory);
      }

      const res = await api.get(`/projects?${query.toString()}`);

      setProjects(res.data.items || []);
      setPages(res.data.pages || 1);
    } catch (err) {
      toast.error(err.cleanMessage || "Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    loadProjects();
  }, [page, selectedCategory]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(".hero-badge", {
        opacity: 0,
        y: 30,
        duration: 0.8,
        ease: "power3.out",
      });

      gsap.from(".hero-title", {
        opacity: 0,
        y: 80,
        duration: 1.1,
        ease: "power4.out",
      });

      gsap.from(".hero-text", {
        opacity: 0,
        y: 40,
        duration: 0.9,
        delay: 0.25,
        ease: "power3.out",
      });

      gsap.from(".project-card", {
        opacity: 0,
        y: 60,
        duration: 1,
        stagger: 0.12,
        ease: "power3.out",
      });

      gsap.to(".hero-orb-one", {
        y: 40,
        x: -25,
        repeat: -1,
        yoyo: true,
        duration: 6,
        ease: "sine.inOut",
      });

      gsap.to(".hero-orb-two", {
        y: -35,
        x: 25,
        repeat: -1,
        yoyo: true,
        duration: 7,
        ease: "sine.inOut",
      });
    }, pageRef);

    return () => ctx.revert();
  }, [projects]);

  const handleCategory = (categoryId) => {
    setSelectedCategory(categoryId);
    setPage(1);
  };

  return (
    <main ref={pageRef}>
      <section className="relative overflow-hidden">
        <div className="relative min-h-[640px] bg-[#04160a] pt-28 pb-28 overflow-hidden">
          <img
            src={HERO_IMAGE}
            alt="AKSUBEB Projects"
            className="absolute inset-0 w-full h-full object-cover"
          />

          <div className="absolute inset-0 bg-[#04160a]/80" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#04160a]/95 via-[#06183f]/75 to-[#ff6600]/35" />

          <div className="hero-orb-one absolute -top-24 -left-24 w-[460px] h-[460px] rounded-full bg-[#06183f]/35 blur-3xl" />
          <div className="hero-orb-two absolute -bottom-28 -right-24 w-[460px] h-[460px] rounded-full bg-[#ff6600]/30 blur-3xl" />

          <div className="absolute inset-0 opacity-[0.08] bg-[linear-gradient(to_right,#fff_1px,transparent_1px),linear-gradient(to_bottom,#fff_1px,transparent_1px)] bg-[size:80px_80px]" />

          <div className="relative max-w-7xl mx-auto px-6 text-center">
            <div className="hero-badge inline-flex items-center gap-3 mb-5">
              <span className="w-10 h-[3px] bg-[#ff6600]" />
              <span className="text-[#ff6600] font-bold tracking-[0.2em] text-sm uppercase">
                Infrastructure Development
              </span>
            </div>

            <h1
              className="hero-title text-5xl md:text-7xl lg:text-[82px] font-black text-white leading-[1.02]"
              style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
            >
              Our Projects
            </h1>

            <p className="hero-text text-white/85 max-w-3xl mx-auto mt-6 leading-relaxed text-[17px]">
              We are committed to providing conducive learning environments
              through the construction, renovation and rehabilitation of schools
              across Akwa Ibom State.
            </p>

            <div className="hero-text flex items-center justify-center gap-2 mt-6 text-sm text-white/80 flex-wrap">
              <Link to="/" className="hover:text-[#ff6600] transition-colors">
                Home
              </Link>

              <ChevronRight size={14} />

              <span className="text-[#ff6600]">Projects</span>
            </div>
          </div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-24">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <h2
              className="text-4xl md:text-5xl font-black text-[#06183f]"
              style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
            >
              All Projects
            </h2>

            <p className="text-slate-600 mt-4 leading-relaxed">
              Explore completed, ongoing and planned AKSUBEB infrastructure
              projects across the State.
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-3 mb-12">
            <button
              onClick={() => handleCategory("")}
              className={`px-5 py-3 rounded-2xl font-black transition ${
                selectedCategory === ""
                  ? "bg-[#06183f] text-white"
                  : "bg-slate-100 text-[#06183f] hover:bg-[#06183f] hover:text-white"
              }`}
            >
              All Projects
            </button>

            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => handleCategory(cat.id)}
                className={`px-5 py-3 rounded-2xl font-black transition ${
                  selectedCategory === cat.id
                    ? "text-white"
                    : "bg-slate-100 text-[#06183f] hover:text-white"
                }`}
                style={{
                  backgroundColor:
                    selectedCategory === cat.id ? cat.color || "#06183f" : "",
                }}
              >
                {cat.name}
              </button>
            ))}
          </div>

          {loading ? (
            <p className="text-center text-slate-500">Loading projects...</p>
          ) : projects.length === 0 ? (
            <p className="text-center text-slate-500">
              No project found in this category.
            </p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {projects.map((project) => (
                <article
                  key={project.id}
                  className="project-card bg-white rounded-[28px] overflow-hidden border border-slate-100 shadow-[0_18px_45px_rgba(0,0,0,0.10)] group"
                >
                  <div className="relative h-[255px] overflow-hidden">
                    <img
                      src={
                        project.featured_image ||
                        "/assets/images/project1.png"
                      }
                      alt={project.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />

                    {project.category && (
                      <span
                        className="absolute top-5 right-5 px-5 py-3 rounded-full text-white text-xs font-black uppercase shadow-lg"
                        style={{
                          backgroundColor:
                            project.category.color || "#ff6600",
                        }}
                      >
                        {project.category.name}
                      </span>
                    )}
                  </div>

                  <div className="p-6">
                    <h3
                      className="text-2xl font-black text-[#06183f] leading-tight"
                      style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
                    >
                      {project.title}
                    </h3>

                    {project.location && (
                      <p className="flex items-center gap-2 text-sm text-slate-500 mt-3">
                        <MapPin size={15} />
                        {project.location}
                      </p>
                    )}

                    {project.summary && (
                      <p className="text-slate-600 mt-3 leading-relaxed line-clamp-3">
                        {project.summary}
                      </p>
                    )}

                    {project.features?.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-5">
                        {project.features.slice(0, 3).map((item) => (
                          <span
                            key={item}
                            className="px-4 py-2 rounded-xl bg-[#06183f]/5 text-[#06183f] text-xs font-bold"
                          >
                            {item}
                          </span>
                        ))}
                      </div>
                    )}

                    <Link
                      to={`/projects/${project.slug}`}
                      className="inline-flex items-center gap-2 mt-6 text-[#06183f] hover:text-[#ff6600] font-black transition-colors"
                    >
                      View Details <ArrowRight size={18} />
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          )}

          {pages > 1 && (
            <div className="flex justify-center items-center gap-3 mt-14">
              <button
                disabled={page === 1}
                onClick={() => setPage((prev) => prev - 1)}
                className="px-5 py-3 rounded-2xl bg-slate-100 text-[#06183f] font-black disabled:opacity-40"
              >
                Prev
              </button>

              {[...Array(pages)].map((_, index) => (
                <button
                  key={index}
                  onClick={() => setPage(index + 1)}
                  className={`w-12 h-12 rounded-2xl font-black ${
                    page === index + 1
                      ? "bg-[#06183f] text-white"
                      : "bg-slate-100 text-[#06183f]"
                  }`}
                >
                  {index + 1}
                </button>
              ))}

              <button
                disabled={page === pages}
                onClick={() => setPage((prev) => prev + 1)}
                className="px-5 py-3 rounded-2xl bg-slate-100 text-[#06183f] font-black disabled:opacity-40"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </section>
    </main>
  );
};

export default ProjectsPage;